import React from 'react';
import { Modal, Form, Input, Select, DatePicker, Row, Col, Button, Space, Alert, Avatar } from 'antd';
import { 
  UserOutlined, 
  TruckOutlined, 
  ToolOutlined, 
  HomeOutlined, 
  FileTextOutlined,
  CarOutlined,
  EnvironmentOutlined,
  BuildOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { ActivityType } from '../../../../types/field.types';
import { User, EmploymentType } from '../../../../types/user.types';
import { Vehicle } from '../../../../types/vehicle.types';
import { useAvailableVehicles, getVehicleOptionLabel } from './useAvailableVehicles';

const { Option } = Select;
const { TextArea } = Input;
const { RangePicker } = DatePicker;

interface ActivityFormModalProps {
  visible: boolean;
  onCancel: () => void;
  onFinish: (values: any) => void;
  form: any;
  users: User[];
  vehicles: Vehicle[];
  isFieldWorker: boolean;
  selectedEmployee: User;
  title: string;
}

const ActivityFormModal: React.FC<ActivityFormModalProps> = ({
  visible,
  onCancel,
  onFinish,
  form,
  users,
  vehicles,
  isFieldWorker,
  selectedEmployee,
  title
}) => {
  // Seçili tarih aralığında çalışanın kullanabileceği araçlar (zimmetli + boştaki havuz araçları)
  const { vehicles: availableVehicles, loading: vehiclesLoading, hasDates } = useAvailableVehicles(form, {
    enabled: visible,
    userId: selectedEmployee?.id
  });

  // Cinsiyete göre varsayılan avatar
  const getDefaultAvatar = () => {
    if (selectedEmployee.profilePicture && selectedEmployee.profilePicture.trim() !== '') {
      return selectedEmployee.profilePicture;
    }
    if (selectedEmployee.gender === 'male') return '/assets/img/man.png';
    if (selectedEmployee.gender === 'female') return '/assets/img/woman.png';
    return undefined;
  };

  const defaultAvatar = getDefaultAvatar();

  return (
    <Modal
      title={title}
      open={visible}
      onCancel={onCancel}
      footer={null}
      width={700}
    >
      <Form form={form} layout="vertical" onFinish={onFinish}>
        {/* Seçili Kullanıcı Bilgisi */}
        <Alert
          message={
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <Avatar
                size={40}
                src={defaultAvatar}
                icon={!defaultAvatar ? <UserOutlined /> : undefined}
                style={{
                  backgroundColor: defaultAvatar 
                    ? '#f0f0f0'
                    : (selectedEmployee.employeeType === EmploymentType.FIELD ? '#1890ff' : '#52c41a')
                }}
              >
                {!defaultAvatar && `${selectedEmployee.firstName[0]}${selectedEmployee.lastName[0]}`}
              </Avatar>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <strong>Atanan Personel:</strong> {selectedEmployee.firstName} {selectedEmployee.lastName}
                  {selectedEmployee.employeeType === EmploymentType.FIELD ? (
                    <EnvironmentOutlined style={{ color: '#1890ff' }} />
                  ) : (
                    <BuildOutlined style={{ color: '#52c41a' }} />
                  )}
                </div>
                <div style={{ fontSize: '12px', opacity: 0.8 }}>
                  {selectedEmployee.email}
                </div>
              </div>
            </div>
          }
          type="info"
          style={{ marginBottom: '16px' }}
        />

        <Form.Item
          name="title"
          label="Aktivite Başlığı"
          rules={[{ required: true, message: 'Lütfen aktivite başlığı girin!' }]}
        >
          <Input placeholder="Örn: Müşteri ziyareti" />
        </Form.Item>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="type"
              label="Aktivite Tipi"
              rules={[{ required: true, message: 'Lütfen aktivite tipi seçin!' }]}
            >
              <Select placeholder="Tip seçin">
                <Option value={ActivityType.VISIT}>
                  <Space><UserOutlined /> Ziyaret</Space>
                </Option>
                <Option value={ActivityType.DELIVERY}>
                  <Space><TruckOutlined /> Teslimat</Space>
                </Option>
                <Option value={ActivityType.MAINTENANCE}>
                  <Space><ToolOutlined /> Bakım</Space>
                </Option>
                <Option value={ActivityType.OFFICE}>
                  <Space><HomeOutlined /> Ofis</Space>
                </Option>
                <Option value={ActivityType.OTHER}>
                  <Space><FileTextOutlined /> Diğer</Space>
                </Option>
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="dates"
              label="Tarih & Saat Aralığı"
              rules={[{ required: true, message: 'Lütfen tarih aralığı seçin!' }]}
            >
              <RangePicker 
                style={{ width: '100%' }} 
                showTime={{ 
                  format: 'HH:mm',
                  defaultValue: [dayjs().hour(9).minute(0), dayjs().hour(18).minute(0)]
                }}
                format="DD.MM.YYYY HH:mm" 
                placeholder={['Başlangıç tarihi', 'Bitiş tarihi']}
              />
            </Form.Item>
          </Col>
        </Row>

        {/* Araç seçimi - seçilen tarih aralığında müsait araçlar */}
        <Form.Item
          name="vehicleId"
          label={<Space size={4}><CarOutlined /> Araç</Space>}
          extra={!hasDates ? 'Müsait araçları görmek için önce tarih aralığı seçin.' : undefined}
        >
          <Select
            placeholder={hasDates ? 'Araç seçin (opsiyonel)' : 'Önce tarih aralığı seçin'}
            showSearch
            allowClear
            disabled={!hasDates}
            loading={vehiclesLoading}
            notFoundContent={vehiclesLoading ? 'Yükleniyor...' : 'Bu saatlerde müsait araç yok'}
            filterOption={(input, option) =>
              (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
            }
            options={availableVehicles.map((vehicle) => ({
              value: vehicle.id,
              label: getVehicleOptionLabel(vehicle)
            }))}
          />
        </Form.Item>

        <Form.Item name="description" label="Açıklama">
          <TextArea rows={4} placeholder="Aktivite detaylarını yazın..." />
        </Form.Item>

        {isFieldWorker && (
          <Form.Item name="isBillable" valuePropName="checked" initialValue={false}>
            <Space>
              <input type="checkbox" />
              <span>Faturalandırılabilir</span>
            </Space>
          </Form.Item>
        )}

        <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
          <Space>
            <Button onClick={onCancel}>İptal</Button>
            <Button type="primary" htmlType="submit">
              {title.includes('Oluştur') ? 'Oluştur' : 'Güncelle'}
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default ActivityFormModal;

